import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import {
  Alignments,
  Avatar,
  Button,
  Icons,
  Menu,
  Pecten,
  Popover,
  Sizes,
  Text,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector, useDispatch } from 'react-redux';
import { IFullState } from '../../../types/All.type';
import { themeActions } from '../../../store/Theme';
import UserOptions from '../../Auth/UserOptions';
import {
  NewGenesisContainer,
  MainContainer,
  NavBarStyled,
  PectenWrapper,
  NewGenesisHeader,
  AvatarArea,
  NewGenesisSwitchAppButton,
  NewGenesisContentContainer,
  NewGenesisMidSection,
} from './style';

const { MoonSolid, SunSolid, HomeSolid } = Icons;
const NEW_GENESIS_MESSAGING_INBOX_ROUTE = '/new-genesis/messaging/inbox';
const NEW_GENESIS_DASHBOARD_HEALTH_CHECK_ROUTE = '/new-genesis/dashboard';

const renderRouteLabel = (label: string, route: string) => (
  <a
    href={route}
    onClick={(event) => event.preventDefault()}
    style={{ color: 'inherit', textDecoration: 'none' }}
  >
    {label}
  </a>
);

const NEW_GENESIS_MENU_TABS = [
  {
    key: 'dashboard',
    label: renderRouteLabel('Dashboard', NEW_GENESIS_DASHBOARD_HEALTH_CHECK_ROUTE),
    route: NEW_GENESIS_DASHBOARD_HEALTH_CHECK_ROUTE,
  },
  {
    key: 'messaging',
    label: renderRouteLabel('Messaging', NEW_GENESIS_MESSAGING_INBOX_ROUTE),
    route: NEW_GENESIS_MESSAGING_INBOX_ROUTE,
  },
  {
    key: 'positions',
    label: renderRouteLabel('Positions', '/new-genesis/positions/balances'),
    route: '/new-genesis/positions/balances',
  },
  {
    key: 'system-admin',
    label: renderRouteLabel('System Admin', '/new-genesis/system-admin/markets'),
    route: '/new-genesis/system-admin/markets',
  },
];

const NewGenesisLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState<string>('dashboard');

  const { isDark } = useSelector((state: IFullState) => state.Theme);
  const { userName, mail, isAuthorized } = useSelector(
    (state: IFullState) => state.genesisUserRolePermission,
  );

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'New Genesis';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const tabKey = pathSegments[2] || 'dashboard';
    setCurrentTab(tabKey);
  }, [location]);

  const handleTabChange = (value: string) => {
    if (currentTab === value) {
      return;
    }

    const tab = NEW_GENESIS_MENU_TABS.find((item) => item.key === value);

    if (tab) {
      setCurrentTab(value);
      navigate(tab.route);
    }
  };

  const handleThemeChange = (value: boolean) => {
    dispatch(
      themeActions.updateTheme({
        isDark: value,
      }),
    );
  };

  const handleBackToLanding = () => {
    navigate(isAuthorized ? '/' : NEW_GENESIS_DASHBOARD_HEALTH_CHECK_ROUTE);
  };

  return (
    <NewGenesisContainer id='new-genesis-layout-container'>
      <MainContainer>
        <NavBarStyled
          data-testid='new-genesis-navbar'
          pectenArea={
            <PectenWrapper>
              <Pecten
                padding='minimum'
                size={Sizes.Medium}
                aria-label='Pecten Logo'
                title='Pecten Logo'
              />
              <NewGenesisHeader flexDirection='column'>
                <Text size='small'>Shell Energy</Text>
                <Text>
                  <strong>New Genesis</strong>
                </Text>
              </NewGenesisHeader>
            </PectenWrapper>
          }
          elevation={false}
          itemsAlignment={Alignments.Center}
          avatarArea={
            <AvatarArea>
              <Button
                size='medium'
                variant='transparent'
                iconOnly
                onClick={() => handleThemeChange(!isDark)}
                icon={isDark ? <SunSolid /> : <MoonSolid />}
                data-testid='theme-toggle-button'
                aria-label='Toggle theme'
              />
              <Popover arrow popup={<UserOptions />} popupPlacement='bottomRight'>
                <div className='pointer'>
                  <Avatar
                    size={Sizes.Medium}
                    label={userName}
                    secondaryLabel={mail}
                    aria-label={userName}
                  />
                </div>
              </Popover>
            </AvatarArea>
          }
        >
          <div className='new-genesis-center-component'>
            {isAuthorized && (
              <NewGenesisSwitchAppButton>
                <Button
                  variant='transparent'
                  onClick={handleBackToLanding}
                  icon={<HomeSolid />}
                  size='large'
                  iconOnly
                  className='new-genesis-switch-button'
                  data-testid='home-button'
                  aria-label='Switch Application'
                />
              </NewGenesisSwitchAppButton>
            )}
            <Menu
              mode='horizontal'
              emphasis
              items={NEW_GENESIS_MENU_TABS}
              activeKey={currentTab}
              size={Sizes.Large}
              selectedKeys={[currentTab]}
              onSelect={(selected) => handleTabChange(selected.key)}
              className='new-genesis-left-menu'
            />
          </div>
        </NavBarStyled>
      </MainContainer>

      <NewGenesisMidSection>
        <NewGenesisContentContainer>
          <Outlet />
        </NewGenesisContentContainer>
      </NewGenesisMidSection>
    </NewGenesisContainer>
  );
};

export default NewGenesisLayout;
